import { useCallback } from 'react';

import { useI18n } from '@change-corgi/core-react/i18n';

import { useCampaignTracking } from '#src/app/shared/hooks/campaignTracking';
import { type SupportedVideoMimeType, useSupportedVideoMimeType } from '#src/app/shared/hooks/media';

import { useVideoContext, useVideosComponentContext } from '../../../../../context';
import { useVideoRecorderActions } from '../../../../../shared/hooks/useVideoRecorderActions';
import { useIsVideoDurationValid } from '../../../../VideoRecorder/hooks/useIsVideoValid';
import type { Payload } from '../types';

import { useSave } from './useSave';
import { useUpload } from './useUpload';

const makeVideoPayload = (blob: Blob, mimeType: SupportedVideoMimeType): Payload => ({
	filename: mimeType.includes('webm') ? '123.webm' : '123.mp4',
	type: mimeType,
	blob,
});

type Result = ModelHookResult<
	{
		isUploading: boolean;
		blob: Blob | null;
	},
	{
		startUploadProcess: () => void;
		prepareCamera: () => void;
	}
>;

/**
 * This hook manages the steps of the video upload process. This includes:
 *   1. Uploading the video to S3
 *   2. Saving the video and thumbnail data (S3 URL and empty string) to backend
 *   3. Reporting any errors in this process
 * See: UploadStep (src/app/pages/actions/components/Videos/types.ts)
 */
// eslint-disable-next-line max-lines-per-function
export function useUploadProcess(): Result {
	const {
		data: { blob, recordedDurationSec },
	} = useVideoContext();
	const {
		data: { isUploading, videoSource },
		actions: { setUploadStep, setErrorMessage, setIsUploading },
	} = useVideosComponentContext();
	const { isVideoDurationValid } = useIsVideoDurationValid();
	const { upload } = useUpload();
	const { createPetitionVideo } = useSave();
	const {
		actions: { prepareCamera },
	} = useVideoRecorderActions();
	const { track, trackError } = useCampaignTracking();
	const supportedMimeType = useSupportedVideoMimeType();
	const { translate } = useI18n();
	const userVisibleErrorMessage = translate('corgi.petition_actions.supporter_voice_video.submit.errors.upload_failed');

	// Backstop for the recorder's own cutoff. Videos chosen from the device were already checked against
	// the same limit by `useIsVideoValid` when they were picked.
	const getDurationError = useCallback(
		async (video: Blob): Promise<string | null> => {
			if (videoSource !== 'recorder') {
				return null;
			}

			const durationCheck = await isVideoDurationValid(video, recordedDurationSec);

			if (durationCheck.durationSource === 'unknown') {
				// Neither the blob nor the recorder could say how long this is, so the backstop waved it
				// through without actually checking anything. Sentry rather than analytics: if probes start
				// failing at scale the limit is unenforced, and that needs to page someone.
				trackError('campaign_action_video_duration_error', new Error('Video duration could not be measured'), {
					params: { fileSize: video.size, mimeType: supportedMimeType },
				});
			}

			if (durationCheck.isValid) {
				return null;
			}

			// Reaching here means both the recorder's timer and its backgrounding handler failed to stop the
			// recording, so any volume at all is worth investigating. Reported to both sinks deliberately:
			// the analytics event carries the structured payload for rate tracking, and Sentry is what
			// actually raises an alert without someone watching a dashboard.
			track('create_video_duration_rejected', {
				duration: durationCheck.duration,
				duration_source: durationCheck.durationSource,
			});
			trackError('campaign_action_video_duration_error', new Error('Recorded video exceeded the duration limit'), {
				params: { duration: durationCheck.duration, durationSource: durationCheck.durationSource },
			});

			return durationCheck.message;
		},
		[isVideoDurationValid, recordedDurationSec, supportedMimeType, track, trackError, videoSource],
	);

	const startUploadProcess = useCallback(() => {
		// oxlint-disable-next-line max-statements -- exceeds the 18-statement limit; flagged as documented debt rather than refactored, per team decision
		const uploadVideo = async () => {
			setIsUploading(true);
			try {
				if (!blob) {
					throw new Error('Unable to upload video due to empty content');
				}

				const durationError = await getDurationError(blob);
				if (durationError) {
					setErrorMessage(durationError);
					setIsUploading(false);
					return;
				}

				const url = await upload(makeVideoPayload(blob, supportedMimeType));
				try {
					await createPetitionVideo({ thumbnailUrl: '', videoUrl: url });
					setUploadStep('success');
					setIsUploading(false);
				} catch (e) {
					trackError('campaign_action_video_error', e, { params: { videoUrl: url } });
					setErrorMessage(userVisibleErrorMessage);
					setIsUploading(false);
				}
			} catch (e) {
				trackError('campaign_action_video_error', e, {
					params: { fileSize: blob?.size, mimeType: supportedMimeType },
				});
				setErrorMessage(userVisibleErrorMessage);
				setIsUploading(false);
			}
		};

		setErrorMessage(null);
		void uploadVideo();
	}, [
		blob,
		createPetitionVideo,
		getDurationError,
		setErrorMessage,
		setIsUploading,
		setUploadStep,
		supportedMimeType,
		trackError,
		upload,
		userVisibleErrorMessage,
	]);

	return {
		data: {
			isUploading,
			blob,
		},
		actions: {
			startUploadProcess,
			prepareCamera,
		},
	};
}
